import { z } from 'zod';
import { ALL_TENANT_ROLES } from './enums';

/**
 * Região Firebase onde vivem os dados de um tenant (banco Firestore, bucket, RTDB, Functions).
 *
 * Origem: #960 — 2026-10-28 22:04 +0100 (segundo nó em `europe-west1`, F1 #1077).
 *
 * Por quê: a América do Sul fica no nó original (`southamerica-east1`, banco `(default)`); o
 * resto do mundo vai para a Bélgica (`europe-west1`, banco nomeado `eu`) para cortar latência.
 * O valor é fixado no tenant na criação e não muda fora do script de migração da F8 (#1086).
 * **Ausência do campo significa `SOUTHAMERICA_EAST1`** — todo tenant criado antes da #960 está
 * em SA e nunca recebe backfill.
 *
 * Contrato: union fechada de dois valores. Valor desconhecido lido do Firestore NÃO pode cair em
 * SA por omissão (gravaria um tenant europeu no banco errado): quem lê bloqueia e reporta.
 *
 * Usado por: Functions (`apps/functions/src/lib/regionalDb.ts` — roteamento de banco, bucket e
 * RTDB; `lib/regionsConfig.ts` — região do tenant novo; criação de tenant em
 * `billing/provisionTenantForSale.ts`, `iap/selfServiceProvisioning.ts`,
 * `selfService/createSelfServiceCheckout.ts`, `admin/createCourtesyTenant.ts`;
 * `admin/setMarketConfig.ts`); `packages/types` (`TenantSchema.region`, `MarketConfigSchema.region`,
 * `CrmUserSchema.region`, `TenantProvisionInput.region`, enum fechado no contrato iOS gerado);
 * admin web (aba Países: `features/countries/{CountriesTab,CountryEditDialog,regionLabels}`) — F2
 * #1078; desde a F3 (#1079): `lib/tenantDirectory.ts`, `lib/tenantMirrors.ts` e os schemas do
 * diretório e do índice neste arquivo. Próximas fases: clientes web, Android e iOS (F5 #1081-#1083), seletor de região do CRM
 * (F6 #1084).
 *
 * Arquitetura: a região é dado do tenant (fonte da verdade: o documento), nunca derivada do
 * prefixo do `tenantId` nem do país em tempo de leitura — mudar o mapa de países só afeta
 * tenants novos.
 *
 * Issues: #960 (desenho), #1076 (sondas), #1077 (F1), #1078 (F2 — campo no tenant, mapa de países).
 */
export const TenantRegionSchema = z.enum(['southamerica-east1', 'europe-west1']);
export type TenantRegion = z.infer<typeof TenantRegionSchema>;

/**
 * As regiões como constantes (`TenantRegion.SOUTHAMERICA_EAST1`), nunca string solta.
 *
 * Origem: #960 — 2026-10-28 22:04 +0100.
 *
 * Usado por: Functions — `lib/regionalDb.ts`, os triggers que fixam o banco do evento
 * (`onLabelWritten`, `onProductWritten`, `billing/onInvoiceWritten`, `checklists/*`,
 * `webhooks/triggers` etc.), `lib/dailyOpsQueue`, `lib/billing/invoiceNumber` (série fiscal),
 * `publicApi/handlers/listTenants` (cursor por região); CRM — `apps/crm-functions/src/lib/db.ts`
 * (#960). Lista por `rg -l '\bTenantRegion\b' apps`.
 */
export const TenantRegion = {
  SOUTHAMERICA_EAST1: 'southamerica-east1',
  EUROPE_WEST1: 'europe-west1',
} as const satisfies Record<string, TenantRegion>;

/** Região de quem não tem o campo: todo tenant anterior à #960. */
export const DEFAULT_TENANT_REGION: TenantRegion = TenantRegion.SOUTHAMERICA_EAST1;

/**
 * Nome do banco Firestore de cada região (`(default)` em SA, `eu` na Bélgica).
 *
 * Origem: #960 — 2026-10-28 22:04 +0100.
 *
 * Usado por: Functions — `lib/regionalDb.ts`, `lib/dailyOpsQueue` (banco da mensagem Pub/Sub),
 * `onLabelWritten`; CRM — `apps/crm-functions/src/lib/db.ts`; espelhado à mão em
 * `scripts/lib/regionDb.mjs` e, já em id de banco, em `apps/psp-ingress-functions/src/lib/db.ts` —
 * paridade cobrada por `apps/functions/src/lib/__tests__/regionListParity.test.ts` (#1077). Os
 * clientes (F5) abrirão a instância com o mesmo nome.
 */
export const FIRESTORE_DATABASE_BY_REGION: Readonly<Record<TenantRegion, string>> = {
  [TenantRegion.SOUTHAMERICA_EAST1]: '(default)',
  // Bug corrigido em 2026-10-30 23:44 +0100 (#960, #1080): o id era `eu`, que o emulador aceita mas a
  // produção recusa (`database_id should be 4-63 characters`, `[a-z0-9-]`) — o provision não criou o
  // banco e o gate de índices do release reprovou. Não voltar a um id curto: ele só falha em produção.
  [TenantRegion.EUROPE_WEST1]: 'europe-west1',
};

/**
 * Bucket Storage de cada região, no projeto `etiquetachef` de produção.
 *
 * Origem: #1080 — 2026-10-29 14:41 +0100 (F4 da #960, infra da Bélgica).
 *
 * Por quê: arquivo de tenant europeu mora no bucket europeu. O nome não é o padrão
 * `<projeto>.firebasestorage.app`, porque esse domínio só vale para o bucket default; um bucket
 * extra ligado ao Firebase usa nome GCS comum. O bucket é criado por `scripts/provision-eu-node.sh`
 * (o owner roda, R1 da #960). Nome de bucket é global e não se renomeia: mudar aqui exige criar
 * outro bucket e migrar.
 *
 * Usado por: Functions — `lib/regionalDb.ts` (`bucketForRegion`), `storage/storageUsage.ts`
 * (bucket dos triggers `*Eu`), `backup/storageBackup.ts`; `firebase.json` (lista `storage`, à
 * mão); clientes na F5 (#1081–#1083). No emulador (projeto `demo-…`), o SA é o bucket default do
 * projeto do slot: quem roda no emulador usa `getStorage().bucket()` para SA, nunca o nome daqui.
 */
export const STORAGE_BUCKET_BY_REGION: Readonly<Record<TenantRegion, string>> = {
  [TenantRegion.SOUTHAMERICA_EAST1]: 'etiquetachef.firebasestorage.app',
  [TenantRegion.EUROPE_WEST1]: 'etiquetachef-eu',
};

/**
 * Instância do Realtime Database (presença dos aparelhos) de cada região, com a URL.
 *
 * Origem: #1080 — 2026-10-29 14:41 +0100 (F4 da #960).
 *
 * Por quê: a instância default mora em `us-central1` (domínio `firebaseio.com`); a europeia em
 * `europe-west1`, e instância fora de `us-central1` tem URL `<instância>.<região>.firebasedatabase.app`.
 * A instância é criada por `scripts/provision-eu-node.sh` (R1 da #960).
 *
 * Usado por: Functions — `lib/regionalDb.ts` (`rtdbForRegion`), `onPresenceWritten` (instância dos
 * triggers); `firebase.json` (lista `database`, à mão); clientes na F5 (presença no Android).
 */
export const RTDB_INSTANCE_BY_REGION: Readonly<Record<TenantRegion, { instance: string; url: string }>> = {
  [TenantRegion.SOUTHAMERICA_EAST1]: {
    instance: 'etiquetachef-default-rtdb',
    url: 'https://etiquetachef-default-rtdb.firebaseio.com',
  },
  [TenantRegion.EUROPE_WEST1]: {
    instance: 'etiquetachef-eu-rtdb',
    url: 'https://etiquetachef-eu-rtdb.europe-west1.firebasedatabase.app',
  },
};

/**
 * Todas as regiões, na ordem do enum — para `z.enum`, laços e listas de opções.
 *
 * Origem: #1078 — 2026-10-29 09:31 +0100 (F2 da #960).
 *
 * Usado por: admin — `features/countries/{CountryEditDialog,CountriesTab}` (opções do `Select` e do
 * filtro de região); Functions — `apps/functions/scripts/create-seed-tenant.ts` (região do
 * `--database`); os testes de contrato.
 */
export const ALL_TENANT_REGIONS: readonly TenantRegion[] = TenantRegionSchema.options;

/**
 * Países atendidos por São Paulo (`southamerica-east1`): a América do Sul, nos locais da Play.
 *
 * Origem: #1078 — 2026-10-29 09:31 +0100 (F2 da #960; tabela "Nós e países" do corpo da #960).
 *
 * Por quê: a regra de padrão é uma só — América do Sul fica em SA, todo o resto vai para a
 * Bélgica. Ela mora aqui para que o servidor (`resolveTenantRegion`) e a aba Países do admin
 * (coluna "Região" e o grupo `southAmerica` de `PLAY_LOCATIONS_BY_CONTINENT`) leiam a MESMA
 * lista; com duas cópias a coluna do admin podia mostrar uma região e o tenant nascer em outra.
 *
 * Usado por: `defaultRegionForCountry` (este arquivo); admin —
 * `apps/admin/src/features/countries/playStoreCountries.ts` (grupo `southAmerica`).
 *
 * Dicas: FK (Malvinas) e GY/SR entram por serem locais da Play no continente; a Guiana Francesa
 * (GF) não é local da Play e cai na regra geral se aparecer. Mudar a lista só afeta tenants
 * NOVOS — a região é fixada na criação (#960).
 */
export const SOUTH_AMERICA_COUNTRIES: readonly string[] = [
  'AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'FK', 'GY', 'PE', 'PY', 'SR', 'UY', 'VE',
];

/**
 * Região padrão de um país, sem override nem chave: América do Sul → SA, resto → Bélgica.
 *
 * Origem: #1078 — 2026-10-29 09:31 +0100 (F2 da #960).
 *
 * Contrato: `country` em ISO alpha-2 (maiúsculas). País desconhecido NÃO é erro — cai na regra
 * geral (`europe-west1`), igual ao resto do mundo.
 *
 * Usado por: `resolveTenantRegion` (este arquivo); `schemas.ts` (texto do `MarketConfigSchema`);
 * admin — `features/countries/{countryRows,CountryEditDialog,playStoreCountries}` (região efetiva,
 * opção "Padrão do grupo" e o grupo da América do Sul); Functions — `admin/setMarketConfig.ts`.
 */
export function defaultRegionForCountry(country: string): TenantRegion {
  return SOUTH_AMERICA_COUNTRIES.includes(country.toUpperCase())
    ? TenantRegion.SOUTHAMERICA_EAST1
    : TenantRegion.EUROPE_WEST1;
}

/**
 * Doc `config/regions` — chave global que habilita a criação de tenants fora de São Paulo.
 *
 * Origem: #1078 — 2026-10-29 09:31 +0100 (F2 da #960).
 *
 * Por quê: o mapa país → região pode ser preparado no admin antes de a Bélgica existir; esta
 * chave é o que decide se ele vale. Desligada (ou doc ausente), todo tenant nasce em
 * `southamerica-east1`, qualquer que seja o país — é também o rollback de qualquer etapa da #960.
 *
 * Dados — `config/regions` (Firestore `(default)`), escrito só pelo callable `setRegionsConfig`
 * (super-admin ROOT); lido pelas Functions (`lib/regionsConfig.ts`, cache de 5 min) e pela aba
 * Países do admin (rules: leitura de super-admin, escrita negada):
 *   {
 *     "europeWest1Enabled": false,
 *     "updated_at": 1793259060000,      // epoch ms do servidor, como em `config/markets`
 *     "updated_by": "uid-do-admin"
 *   }
 *
 * Usado por: Functions — `lib/regionsConfig.ts`, `admin/setRegionsConfig.ts`; admin — aba Países
 * (`features/countries/RegionsSwitch.tsx`).
 *
 * Dicas: o `setRegionsConfig` recusa ligar a chave enquanto `europe-west1` não estiver em
 * `ENABLED_TENANT_REGIONS` das Functions — senão toda venda fora da América do Sul lançaria
 * `region_not_enabled`. Desde a F4 (#1080) a Bélgica está habilitada e ligar é aceito; não há trava
 * de criação no código (owner, 2026-10-29): a ordem do rollout (clientes da F5 nas lojas antes) é
 * do owner.
 */
export const RegionsConfigSchema = z.object({
  europeWest1Enabled: z.boolean(),
  updated_at: z.number().int().positive().optional(),
  updated_by: z.string().min(1).optional(),
});
export type RegionsConfig = z.infer<typeof RegionsConfigSchema>;

/**
 * Input do callable `setRegionsConfig` (aba Países do admin, super-admin ROOT).
 *
 * Origem: #1078 — 2026-10-29 09:31 +0100 (F2 da #960).
 *
 * Usado por: Functions — `admin/setRegionsConfig.ts`; admin — `lib/callables.ts`
 * (`setRegionsConfigCallable`).
 */
export const SetRegionsConfigInput = z.object({
  europeWest1Enabled: z.boolean(),
  reason: z.string().trim().min(1).max(500),
});
export type SetRegionsConfigInput = z.infer<typeof SetRegionsConfigInput>;

/**
 * Região em que um tenant NOVO deve nascer — função pura, sem leitura.
 *
 * Origem: #1078 — 2026-10-29 09:31 +0100 (F2 da #960).
 *
 * Por quê: a decisão tem três entradas (país, override do país em `config/markets` e a chave
 * `config/regions`) e três caminhos de criação que a consultam (venda, self-service, cortesia; o
 * seed grava a região do banco escolhido, sem resolver); centralizar evita que um deles esqueça a
 * chave e crie tenant na Bélgica antes da hora.
 *
 * Contrato: chave desligada ou config ausente → `southamerica-east1` SEMPRE. Ligada →
 * `market.region` quando definido, senão `defaultRegionForCountry(country)`. Não valida se a
 * região tem infraestrutura — isso é do chamador (`resolveRegionForNewTenant` nas Functions, que
 * confere `ENABLED_TENANT_REGIONS` e lança).
 *
 * Usado por: Functions — `lib/regionsConfig.ts` (`resolveRegionForNewTenant`), que alimenta
 * `provisionTenantForSale`, `iap/selfServiceProvisioning`, `selfService/createSelfServiceCheckout`
 * e `admin/createCourtesyTenant`.
 *
 * Arquitetura: a região resolvida é gravada no tenant (e antes no marcador/reserva) e nunca é
 * recalculada para tenant existente — mudar o mapa ou a chave só afeta tenants novos (#960).
 */
export function resolveTenantRegion(
  country: string,
  market: { region?: TenantRegion | null } | null,
  regionsConfig: { europeWest1Enabled: boolean } | null,
): TenantRegion {
  if (!regionsConfig?.europeWest1Enabled) return DEFAULT_TENANT_REGION;
  return market?.region ?? defaultRegionForCountry(country);
}

/**
 * Nomes das coleções globais da F3 da #960, todas no banco `(default)`.
 *
 * Origem: #1079 — 2026-10-29 11:56 +0100 (F3 da #960).
 *
 * Por quê: a mesma string aparece em Functions, CRM, scripts e rules; a constante evita o erro de
 * digitação que criaria uma coleção paralela sem aviso (as rules comparam o literal à mão).
 *
 * Usado por: Functions — `lib/regionalDb.ts`, `lib/tenantDirectory.ts`, `lib/tenantMirrors.ts`;
 * CRM — `apps/crm-functions/src/lib/db.ts`. Espelhado à mão em `scripts/lib/regionDb.mjs` (`.mjs`
 * puro) e no literal de `firebase/firestore.rules`.
 */
export const TENANT_REGIONS_COLLECTION = 'tenantRegions';
/** Ver `TENANT_REGIONS_COLLECTION`. Subcoleção: `userTenantIndex/{uid}/tenants/{tid}`. */
export const USER_TENANT_INDEX_COLLECTION = 'userTenantIndex';
/** Ver `TENANT_REGIONS_COLLECTION`. Subcoleção: `tenantAccess/{tid}/memberAccess/{uid}`. */
export const TENANT_ACCESS_COLLECTION = 'tenantAccess';
/**
 * Subcoleção dos membros no espelho de acesso: `tenantAccess/{tid}/memberAccess/{uid}`.
 *
 * Origem: #1079 — 2026-10-29 12:33 +0100 (auditoria da F3, achado 1).
 *
 * Por quê NÃO se chama `members`: toda `collectionGroup('members')` do `(default)` passaria a
 * enxergar o espelho — `aggregateDailyMetrics` contaria o membro europeu duas vezes,
 * `searchPlatformUsers` o listaria em dobro e o match `/{path=**}/members/{memberUid}` das rules
 * abriria o espelho ao super-admin. Não renomear para `members`.
 *
 * Usado por: Functions — `lib/tenantMirrors.ts`; `firebase/firestore.rules` (via
 * `tenantAccess/{document=**}`); Storage rules europeias na F4 (#1080).
 */
export const TENANT_ACCESS_MEMBERS_SUBCOLLECTION = 'memberAccess';

/**
 * Doc `tenantRegions/{tenantId}` — diretório de região de um tenant FORA de São Paulo.
 *
 * Origem: #1079 — 2026-10-29 11:56 +0100 (F3 da #960).
 *
 * Por quê: o roteador das Functions (`dbFor`) precisa saber em que banco mora o tenant antes de
 * ler qualquer documento dele, e o único banco que todo mundo enxerga é o `(default)`. A entrada é
 * gravada na RESERVA do id (antes de qualquer documento do tenant), assim o primeiro webhook que
 * chega já resolve o banco certo. Tenant de SA não tem entrada: ausência = SA, desde que
 * `tenants/{tid}` exista no `(default)` (ver `resolveTenantRegionFromDirectory`).
 *
 * Dados — `tenantRegions/{tid}` no `(default)`; escrito só pelas Functions
 * (`lib/tenantDirectory.ts`); lido pelas Functions/CRM/scripts e, por `get`, por qualquer logado
 * (rules; `list` negado):
 *   {
 *     "region": "europe-west1",
 *     "epoch": 1,                  // sobe a cada migração (F8, #1086); hoje o cache só expira pelo TTL
 *     "migrating": true,           // só durante a cópia da F8; ausente no resto do tempo
 *     "created_at": <serverTimestamp>
 *   }
 *
 * Contrato: `region` é a union FECHADA — valor desconhecido reprova o parse e o roteador bloqueia
 * (nunca cai em SA, gravaria no banco errado). `epoch` ≥ 1.
 *
 * Usado por: Functions — `lib/regionalDb.ts` (`resolveTenantRegionFromDirectory`),
 * `lib/tenantDirectory.ts`; CRM — `apps/crm-functions/src/lib/db.ts`.
 *
 * Issues: #960 (desenho), #1079 (F3), #1086 (migração, `epoch`/`migrating`).
 */
export const TenantRegionDirectorySchema = z.object({
  region: TenantRegionSchema,
  epoch: z.number().int().min(1),
  migrating: z.boolean().optional(),
  created_at: z.unknown().optional(),
});
export type TenantRegionDirectory = z.infer<typeof TenantRegionDirectorySchema>;

/**
 * Doc `userTenantIndex/{uid}/tenants/{tenantId}` — vínculo do usuário com um tenant FORA de SA.
 *
 * Origem: #1079 — 2026-10-29 11:56 +0100 (F3 da #960).
 *
 * Por quê: o lookup pós-login dos clientes é uma `collectionGroup('members')` no `(default)`, que
 * não enxerga o banco `europe-west1`. O índice no `(default)` soma os vínculos europeus. Carrega os campos
 * que os clientes leem hoje do doc de membro (web `useMyMemberships.ts`: `displayName`, `email`;
 * Android/iOS: `isDemo`), para a F5 não precisar de uma segunda leitura no `eu`.
 *
 * Dados — escrito só pelas Functions (`lib/tenantMirrors.ts`, a partir de
 * `tenants/{tid}/members/{uid}` no banco do tenant); lido pelo próprio uid (rules):
 *   {
 *     "tenantId": "IT-0000000000",
 *     "role": "OWNER",              // `TenantRole`; null + removed:true = lápide (removido ou papel inválido)
 *     "region": "europe-west1",
 *     "displayName": "Nome",
 *     "email": "",
 *     "isDemo": false,              // ausente no doc de membro = false
 *     "removed": false,
 *     "source_read_time": 1793259060123456,  // readTime da fonte em MICROSSEGUNDOS; corrida fora de ordem
 *     "updated_at": <serverTimestamp>
 *   }
 *
 * Contrato: entrada de membro removido vira lápide (`removed: true`, `role: null`), não some — um
 * handler atrasado não pode ressuscitá-la. Tenant `DELETED` mantém a entrada (o
 * `removedTenantIds`, #969, depende dela); a limpeza é da F6 (#1084).
 *
 * Usado por: Functions — `lib/tenantMirrors.ts`; clientes na F5 (#1081-#1083).
 */
export const UserTenantIndexEntrySchema = z.object({
  tenantId: z.string().min(1),
  role: z.enum(ALL_TENANT_ROLES).nullable(),
  region: TenantRegionSchema,
  displayName: z.string(),
  email: z.string(),
  isDemo: z.boolean(),
  removed: z.boolean(),
  source_read_time: z.number().int().nonnegative(),
  updated_at: z.unknown().optional(),
});
export type UserTenantIndexEntry = z.infer<typeof UserTenantIndexEntrySchema>;

/**
 * Doc `tenantAccess/{tenantId}` — espelho do acesso de um tenant FORA de SA, para as Storage rules.
 *
 * Origem: #1079 — 2026-10-29 11:56 +0100 (F3 da #960; sonda S2 negativa, #1076).
 *
 * Por quê: as Storage rules só leem o `(default)` (`firestore.get` não aceita banco nomeado — S2),
 * e o teto é de 2 `get` por avaliação. Os quatro campos que `paywallOpen` e `hasChecklists` leem
 * de `billing/subscription` (`firebase/storage.rules`) ficam juntos neste doc, cópia CRUA: tipo
 * inesperado vira `null`, que FECHA o paywall (nunca passa pelo `SubscriptionSchema`, cujo
 * `.catch` mudaria a semântica).
 *
 * Dados — escrito só pelas Functions (`lib/tenantMirrors.ts`); nenhum cliente lê (rules); lido
 * pelas Storage rules do bucket europeu na F4 (#1080):
 *   {
 *     "capabilities": ["BASE_ACTIVE"],         // null = sem assinatura / tipo estranho
 *     "commercial_mode": "DIRECT",             // string crua, como as rules comparam
 *     "external_subscription_id": "sub_0000",  // null quando ausente
 *     "installment_plan_id": null,
 *     "source_read_time": 1793259060123456,    // µs (ver `UserTenantIndexEntrySchema`)
 *     "updated_at": <serverTimestamp>
 *   }
 * Membros: `tenantAccess/{tid}/memberAccess/{uid}` = `{ role, removed, source_read_time }` (a
 * subcoleção NÃO se chama `members` — ver `TENANT_ACCESS_MEMBERS_SUBCOLLECTION`).
 *
 * Usado por: Functions — `lib/tenantMirrors.ts`; Storage rules europeias (F4).
 *
 * Dicas: campo novo lido de `billing/subscription` pelas Storage rules entra AQUI no mesmo commit —
 * o teste de paridade `lib/__tests__/tenantMirrors.test.ts` reprova se faltar.
 */
export const TenantAccessMirrorSchema = z.object({
  capabilities: z.array(z.string()).nullable(),
  commercial_mode: z.string().nullable(),
  external_subscription_id: z.string().nullable(),
  installment_plan_id: z.string().nullable(),
  source_read_time: z.number().int().nonnegative(),
  updated_at: z.unknown().optional(),
});
export type TenantAccessMirror = z.infer<typeof TenantAccessMirrorSchema>;

/** `tenantAccess/{tid}/memberAccess/{uid}` — ver `TenantAccessMirrorSchema`. Lápide: `role: null, removed: true`. */
export const TenantAccessMemberSchema = z.object({
  role: z.enum(ALL_TENANT_ROLES).nullable(),
  removed: z.boolean(),
  source_read_time: z.number().int().nonnegative(),
});
export type TenantAccessMember = z.infer<typeof TenantAccessMemberSchema>;
